import React, { useState, useEffect, useCallback } from "react";
import { Autocomplete, TextField, Typography, styled } from "@mui/material";
import { Button, Col, Row, Dropdown, Navbar, Nav, NavDropdown } from "react-bootstrap";
import { IAffilateBody, ILogin, IThemeGalleryReq } from "../types";
import { defaultThemeOptions } from "../defaultTheme";
import Loader from "../Loader";
import { getRedirectUrl, HandleAPIError } from "../commonFunction";
import { faUser, faRotate, faCartFlatbed, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { affiliate, affiliateDataId, clearAffiliate, fetchAffiliateData } from "../slices/affiliateTheme";
import { setPreview } from "../slices/commonSlice/preview";
import {
  affiliateTheme,
  loadSavedTheme,
} from "../slices/commonSlice/themeSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { showConfirm } from "./Swal";
import { auth, authPassword, authPrivilege, authSessionid, authUsername, editor, login, logOut, publisher, user, vmLogin } from "../slices/auth-slice";
import { themeGallery } from "../slices/themeGallery-slice";

// Define styled components
const Title = styled(Typography)(({ theme }) => ({
  fontSize: "20px",
  // lineHeight: theme.typography.h6.fontSize,
  fontFamily: "source-sans-pro"
}));


const LogOutDropdown = styled('div')({
  zIndex: 999,
  display: 'flex',
  justifyContent: 'flex-end',
  padding: ' 0px',
  paddingRight: '25px',
  backgroundColor: '#fff',
});

interface HeaderProps {
  affiliateName?: string;
}

const ThemeGallerySection = styled("div")({
  zIndex: "99",
  display: "flex",
  padding: "0px ",
  marginRight: "26px"
});

const Header: React.FC<HeaderProps> = ({ affiliateName }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const affiliates = useAppSelector((state) => state.fetchAffiliate.affiliate);
  const [showDropdown, setShowDropdown] = useState(false);
  const userStatus = useAppSelector(user);
  const editorStatus = useAppSelector(editor);
  const publisherStatus = useAppSelector(publisher);
  const authStatus = useAppSelector(auth);
  const password = useAppSelector(authPassword);
  const privilege = useAppSelector(authPrivilege);
  const loading = useAppSelector((state) => state.affiliateData.loading);
  const selectedThemeData = useAppSelector((state) => state.affiliateData);
  const sessionId = useAppSelector(authSessionid);
  const userName = useAppSelector(authUsername);
  const themeObject: any = selectedThemeData.preview;
  const [selectedAffiliate, setSelectedAffiliate] = useState<any | null>(null);
  const [themeGalleryLength, setThemeGalleryLength] = useState<number>(0);
  const affiliteId = useAppSelector(affiliateDataId)
    const redirectDomain = getRedirectUrl()


  const styleObj = {
    color: "black",
    zIndex: 1022,
    width: "270px",
  };

  const affiliateOptions = () => {
    return affiliates.map((item: any) => ({
      value: item.id,
      label: item.name,
    }));
  };

  async function logout() {
    let confirmed = await showConfirm(
      "CONFIRM LOGOUT",
      "Are you sure you want to logout?"
    );
    if (confirmed?.isConfirmed) {
      dispatch(logOut());
      dispatch(clearAffiliate());
      if(publisherStatus){
        window.open(`${redirectDomain}?logout-sync=1`);
      }
      // userStatus ? navigate("/user-login") : navigate("/");
    }
  }

  const getAffililateTheme = useCallback(
    async (id?: number) => {
      let affiliateId: number | null = id ?? null;

      const data: IAffilateBody = {
        sessionid: sessionId,
        username: userName,
        request: {
          affiliateid: affiliateId,
        }
      };

      if (affiliateId !== null) {
        try {
          await dispatch(affiliate(data)).unwrap();
        } catch (error) {
          HandleAPIError(error);
        }
      } else {
        dispatch(affiliateTheme(defaultThemeOptions));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const fetchThemes = async () => {
      const request: IThemeGalleryReq = {
        sessionid: sessionId,
        username: userName,
        failstatus: 0,
        request: {}
      };

      if (affiliteId) {
        const response = await dispatch(themeGallery(request)).unwrap();
        setThemeGalleryLength(response?.data?.gallery?.length);
      }
    }
    fetchThemes();
    getAffililateTheme(selectedThemeData?.affiliateid ?? undefined);
  }, [affiliteId])

  useEffect(() => {
    if (selectedThemeData.status === "0") {
      dispatch(setPreview(selectedThemeData.preview));
      if (selectedThemeData.preview === null) {
        dispatch(affiliateTheme(defaultThemeOptions));
      } else {
        if (location.state?.change) {
          dispatch(loadSavedTheme({ theme: location.state?.theme, editorThemeState: true }));
          dispatch(affiliateTheme(themeObject));
        } else {
          dispatch(loadSavedTheme({ theme: themeObject }));
          dispatch(affiliateTheme(themeObject));
        }
      }
    }
  }, [selectedThemeData]);

  useEffect(() => {
    if (selectedThemeData?.affiliateid) {
      setSelectedAffiliate({
        value: selectedThemeData?.affiliateid,
        label: selectedThemeData?.affiliatename,
      });
    }
  }, [selectedThemeData]);

  const fetchAffiliateApi = useCallback(async () => {
    try {
      const data: IAffilateBody = {
        sessionid: sessionId,
        username: userName,
        request: {
          themebuilder: "Y",
        }
      };

      await dispatch(fetchAffiliateData(data));
    } catch (error) {
      HandleAPIError(error);
    }
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      const data: IAffilateBody = {
        sessionid: sessionId,
        username: userName,
        request: {
          themebuilder: "Y",
        }
      };
      if (editorStatus && affiliates.length === 0) {
        await dispatch(fetchAffiliateData(data));
      }
    };
    fetchData();
  }, [editorHeader]);

  function editorHeader() {
    return (
      <Col md={"7"} className="d-flex justify-content-start align-items-center">
        <Title
          // variant="h6"
          className="text-dark font-size-16 align-items-center mx-2"
        >
          Select Affiliate
        </Title>
        <div style={styleObj} className="py-1">
          <Autocomplete
            options={affiliateOptions()}
            getOptionLabel={(option) => option.label}
            className="font-family"
            onChange={(event, value) => {
              getAffililateTheme(value?.value);
              setSelectedAffiliate(value);
              navigate(".", { state: { change: false } });
            }}
            value={selectedAffiliate}
            disableClearable
            renderOption={(props, option) => (
              <li
                {...props}
                key={option.id || option.value}
                style={{ fontSize: '16px', padding: "6px 10px" }}
                className="font-family hover-background"
              >
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select..."
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    fontSize: "16px",
                    height: "32px",
                    cursor: "pointer",
                    borderRadius: "4px",
                    fontFamily: "source-sans-pro",
                    '& input': {
                      cursor: "pointer",
                      padding: '0px !important',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#8da1b2 !important', // Change border color on hover
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2684ff !important', // Change border color on focus
                    },
                  },
                }}
              />
            )}
          />

        </div>
        <Button
          className="btn-success btn-sm py-1 mx-2 text-uppercase ms-3 shadow-lg gap-2 d-flex align-items-center"
          onClick={fetchAffiliateApi}
        >
          Refresh Affiliate List
          <FontAwesomeIcon icon={faRotate} />
        </Button>
      </Col>
    );
  }

  const publisherHeader = () => (
    <>
      <Col md={"6"} className="d-flex justify-content-start">
        {/* <Title className="align-items-center font-size-20 text-dark mx-2  mb-0">
        Theme Builder
      </Title> */}
      </Col>
    </>
  );

  const userHeader = () => (
    <Col md={"8"} className="d-flex justify-content-between align-items-center">
      <Title
        // variant="h6"
        className="text-dark font-size-16 align-items-center mx-2"
      >
       {affiliateName}
      </Title>
    </Col>
  );

  const getdataofHeader = () => {
    if (authStatus && editorStatus) {
      return editorHeader();
    } else if (authStatus && publisherStatus) {
      return publisherHeader();
    } else if (authStatus && userStatus) {
      return userHeader();
    }
    return null;
  };

  const handleToggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  return (
    <>
      <Loader loading={loading} />
      <div className="px-3 bg-white w-100 sticky-top navbarContainer">
        <Row className="align-items-center justify-content-between pt-2 pb-2">
          <div className="d-flex flex-row align-items-center justify-content-between">
            {getdataofHeader()}
            <div className=" d-flex align-items-center ">
              {affiliteId &&
                (themeGalleryLength > 0 &&
                  (userStatus || editorStatus) &&
                  (location.pathname !== "/theme-gallery" ? (
                    <ThemeGallerySection>
                      <Button
                        className="btn-secondary text-uppercase font-size-15 py-1 mx-2 ms-3 shadow-lg btn-grey"
                        onClick={() => navigate("/theme-gallery")}
                      >
                        Theme Gallery
                      </Button>
                    </ThemeGallerySection>
                  ) : (
                    <ThemeGallerySection>
                      <Button
                        className="btn-secondary text-uppercase py-1 mx-2 ms-3 shadow-lg btn-grey"
                        onClick={() => navigate(-1)}
                      >
                        Back
                      </Button>
                    </ThemeGallerySection>
                  )))}
              <LogOutDropdown
                onClick={handleToggleDropdown}
                onMouseEnter={() => setShowDropdown(true)}
                onMouseLeave={() => setShowDropdown(false)}
              >
                <Navbar
                  className="p-0"
                  expand="lg"
                  sticky="top"
                >
                  <Navbar.Collapse id="basic-navbar-nav">
                    <Nav className="me-auto p-0">
                      <NavDropdown
                        title={<span className="small">{userName}</span>}
                        id="basic-nav-dropdown"
                        renderMenuOnMount={true}
                        align="end"
                        show={showDropdown}
                        onToggle={(isOpen) => setShowDropdown(isOpen)}
                        className="underline-disine p-2"
                      >
                        <NavDropdown.Item
                          className="font-size-15 p-2"
                          onClick={logout}
                        >
                          LOGOUT
                        </NavDropdown.Item>
                      </NavDropdown>
                    </Nav>
                  </Navbar.Collapse>
                </Navbar>
              </LogOutDropdown>
            </div>
          </div>
        </Row>
      </div>
    </>
  );
};

export default Header;
